import { createContext, useCallback, useContext, useMemo } from 'react'
import { useAccount } from 'wagmi'
import { useWallet } from '@solana/wallet-adapter-react'
import { useDisplayStore } from './DisplayStoreProvider'
import useEvmActionsImpl from '../hooks/useEvmActions'
import useSolanaActionsImpl from '../hooks/useSolanaActions'

const WalletActionsContext = createContext(null)

export function WalletActionsProvider({ children }) {
  const store = useDisplayStore()
  const {
    action: evmAction,
    isSignWaiting,
    isSwitchingNetwork,
  } = useEvmActionsImpl(store)
  const { action: solAction } = useSolanaActionsImpl(store)
  const { isConnected: evmConnected } = useAccount()
  const { connected: solConnected } = useWallet()

  const isWalletConnected = Boolean(evmConnected || solConnected)

  const action = useCallback(async () => {
    if (evmConnected) return evmAction()
    if (solConnected) return solAction()
    return { success: false }
  }, [evmConnected, solConnected, evmAction, solAction])

  const value = useMemo(
    () => ({
      action,
      isWalletConnected,
      isSignWaiting,
      isSwitchingNetwork,
      evmConnected,
      solConnected,
    }),
    [
      action,
      isWalletConnected,
      isSignWaiting,
      isSwitchingNetwork,
      evmConnected,
      solConnected,
    ],
  )

  return (
    <WalletActionsContext.Provider value={value}>
      {children}
    </WalletActionsContext.Provider>
  )
}

export function useWalletActions() {
  const ctx = useContext(WalletActionsContext)
  if (!ctx) {
    throw new Error('useWalletActions must be used within WalletActionsProvider')
  }
  return ctx
}
